import React, { createContext, useContext, useReducer, useEffect } from 'react';
import toast from 'react-hot-toast';

const InventoryContext = createContext();

// Initial state
const initialState = {
  inventoryItems: [],
  warehouses: [],
  batches: [],
  movements: [],
  loading: false,
  error: null,
  syncStatus: {
    lastSync: null,
    inSync: true,
    pendingOperations: 0
  },
  totalValue: 0,
  commodityBreakdown: {},
  lowStockAlerts: [],
  expiringItems: []
};

// Action types
const INVENTORY_ACTIONS = {
  SET_LOADING: 'SET_LOADING',
  SET_ERROR: 'SET_ERROR',
  LOAD_INVENTORY: 'LOAD_INVENTORY',
  ADD_INVENTORY_ITEM: 'ADD_INVENTORY_ITEM',
  UPDATE_INVENTORY_ITEM: 'UPDATE_INVENTORY_ITEM',
  REMOVE_INVENTORY_ITEM: 'REMOVE_INVENTORY_ITEM',
  ADD_MOVEMENT: 'ADD_MOVEMENT',
  UPDATE_SYNC_STATUS: 'UPDATE_SYNC_STATUS',
  SET_WAREHOUSES: 'SET_WAREHOUSES',
  SET_BATCHES: 'SET_BATCHES',
  UPDATE_COMMODITY_BREAKDOWN: 'UPDATE_COMMODITY_BREAKDOWN',
  SET_ALERTS: 'SET_ALERTS'
};

// Reducer
const inventoryReducer = (state, action) => {
  switch (action.type) {
    case INVENTORY_ACTIONS.SET_LOADING:
      return { ...state, loading: action.payload };
    
    case INVENTORY_ACTIONS.SET_ERROR:
      return { ...state, error: action.payload, loading: false };
    
    case INVENTORY_ACTIONS.LOAD_INVENTORY:
      return {
        ...state,
        inventoryItems: action.payload.items,
        totalValue: action.payload.totalValue,
        commodityBreakdown: action.payload.commodityBreakdown,
        loading: false
      };
    
    case INVENTORY_ACTIONS.ADD_INVENTORY_ITEM:
      return {
        ...state,
        inventoryItems: [...state.inventoryItems, action.payload]
      };
    
    case INVENTORY_ACTIONS.UPDATE_INVENTORY_ITEM:
      return {
        ...state,
        inventoryItems: state.inventoryItems.map(item =>
          item.id === action.payload.id ? { ...item, ...action.payload } : item
        )
      };
    
    case INVENTORY_ACTIONS.REMOVE_INVENTORY_ITEM:
      return {
        ...state,
        inventoryItems: state.inventoryItems.filter(item => item.id !== action.payload)
      };
    
    case INVENTORY_ACTIONS.ADD_MOVEMENT:
      return {
        ...state,
        movements: [action.payload, ...state.movements]
      };
    
    case INVENTORY_ACTIONS.UPDATE_SYNC_STATUS:
      return {
        ...state,
        syncStatus: { ...state.syncStatus, ...action.payload }
      };
    
    case INVENTORY_ACTIONS.SET_WAREHOUSES:
      return { ...state, warehouses: action.payload };
    
    case INVENTORY_ACTIONS.SET_BATCHES:
      return { ...state, batches: action.payload };
    
    case INVENTORY_ACTIONS.UPDATE_COMMODITY_BREAKDOWN:
      return { ...state, commodityBreakdown: action.payload };
    
    case INVENTORY_ACTIONS.SET_ALERTS:
      return {
        ...state,
        lowStockAlerts: action.payload.lowStock || [],
        expiringItems: action.payload.expiring || []
      };
    
    default:
      return state;
  }
};

// Provider component
export const InventoryProvider = ({ children }) => {
  const [state, dispatch] = useReducer(inventoryReducer, initialState);

  // API simulation functions
  const loadInventoryData = async () => {
    dispatch({ type: INVENTORY_ACTIONS.SET_LOADING, payload: true });
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const mockData = {
        items: [
          {
            id: 'INV-001',
            commodityType: 'Sugar',
            grade: 'Raw',
            quantity: 50000,
            unit: 'kg',
            warehouseId: 'WH-001',
            batchNumber: 'BT-2024-001',
            expirationDate: '2025-06-01',
            qualityCertificates: ['QC-001'],
            tokensMinted: 50000,
            status: 'available',
            location: 'Warehouse A - Section 1',
            origin: 'Brazil',
            producer: 'Sugar Corp Brazil',
            receivedDate: '2024-01-15',
            lastInspection: '2024-01-20',
            valuePerUnit: 0.65,
            totalValue: 32500
          },
          {
            id: 'INV-002',
            commodityType: 'Coffee',
            grade: 'Arabica',
            quantity: 25000,
            unit: 'kg',
            warehouseId: 'WH-002',
            batchNumber: 'BT-2024-002',
            expirationDate: '2025-12-01',
            qualityCertificates: ['QC-002', 'QC-003'],
            tokensMinted: 25000,
            status: 'available',
            location: 'Warehouse B - Cold Storage',
            origin: 'Colombia',
            producer: 'Colombian Coffee Co',
            receivedDate: '2024-02-01',
            lastInspection: '2024-02-05',
            valuePerUnit: 3.20,
            totalValue: 80000
          },
          {
            id: 'INV-003',
            commodityType: 'Gold',
            grade: 'Pure999',
            quantity: 1000,
            unit: 'oz',
            warehouseId: 'VAULT-001',
            batchNumber: 'AU-2024-001',
            expirationDate: null,
            qualityCertificates: ['QC-004'],
            tokensMinted: 1000,
            status: 'available',
            location: 'Secure Vault 1',
            origin: 'Canada',
            producer: 'Northern Gold Mining',
            receivedDate: '2024-01-30',
            lastInspection: '2024-01-30',
            valuePerUnit: 2050.00,
            totalValue: 2050000
          }
        ],
        totalValue: 2162500,
        commodityBreakdown: {
          Sugar: { quantity: 50000, value: 32500, percentage: 1.5 },
          Coffee: { quantity: 25000, value: 80000, percentage: 3.7 },
          Gold: { quantity: 1000, value: 2050000, percentage: 94.8 }
        }
      };
      
      dispatch({ type: INVENTORY_ACTIONS.LOAD_INVENTORY, payload: mockData });
      
      // Load warehouses
      const warehouses = [
        { id: 'WH-001', name: 'Warehouse A', location: 'Santos, Brazil', type: 'Standard', capacity: 100000 },
        { id: 'WH-002', name: 'Warehouse B', location: 'Bogotá, Colombia', type: 'Climate Controlled', capacity: 75000 },
        { id: 'VAULT-001', name: 'Secure Vault 1', location: 'Toronto, Canada', type: 'High Security', capacity: 10000 }
      ];
      dispatch({ type: INVENTORY_ACTIONS.SET_WAREHOUSES, payload: warehouses });
      
    } catch (error) {
      dispatch({ type: INVENTORY_ACTIONS.SET_ERROR, payload: error.message });
      toast.error('Failed to load inventory data');
    }
  };

  const addInventoryItem = async (item) => {
    try {
      const newItem = {
        ...item,
        id: `INV-${Date.now()}`,
        receivedDate: new Date().toISOString().split('T')[0],
        status: 'pending_verification'
      };
      
      dispatch({ type: INVENTORY_ACTIONS.ADD_INVENTORY_ITEM, payload: newItem });
      toast.success('Inventory item added successfully');
      
      // Simulate blockchain minting
      await mintTokens(newItem);
      
    } catch (error) {
      toast.error('Failed to add inventory item');
      throw error;
    }
  };

  const mintTokens = async (item) => {
    try {
      // Simulate blockchain interaction
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      const updatedItem = {
        ...item,
        tokensMinted: item.quantity,
        status: 'available'
      };
      
      dispatch({ type: INVENTORY_ACTIONS.UPDATE_INVENTORY_ITEM, payload: updatedItem });
      
      // Add movement record
      const movement = {
        id: `MOV-${Date.now()}`,
        type: 'mint',
        itemId: item.id,
        quantity: item.quantity,
        fromLocation: null,
        toLocation: item.location,
        timestamp: new Date().toISOString(),
        status: 'completed',
        txHash: `cb${Math.floor(Math.random() * 100).toString().padStart(2, '0')}${Math.random().toString(16).substr(2, 40)}`
      };
      
      dispatch({ type: INVENTORY_ACTIONS.ADD_MOVEMENT, payload: movement });
      toast.success(`Tokens minted successfully for ${item.commodityType}`);
      
    } catch (error) {
      toast.error('Failed to mint tokens');
      throw error;
    }
  };

  const moveInventory = async (itemId, fromLocation, toLocation, quantity) => {
    try {
      const movement = {
        id: `MOV-${Date.now()}`,
        type: 'transfer',
        itemId,
        quantity,
        fromLocation,
        toLocation,
        timestamp: new Date().toISOString(),
        status: 'in_progress'
      };
      
      dispatch({ type: INVENTORY_ACTIONS.ADD_MOVEMENT, payload: movement });
      
      // Simulate movement processing
      setTimeout(() => {
        const completedMovement = { ...movement, status: 'completed' };
        dispatch({ type: INVENTORY_ACTIONS.ADD_MOVEMENT, payload: completedMovement });
        toast.success('Inventory movement completed');
      }, 3000);
      
    } catch (error) {
      toast.error('Failed to process inventory movement');
      throw error;
    }
  };

  const checkSyncStatus = async () => {
    try {
      // Simulate blockchain sync check
      const isInSync = Math.random() > 0.1; // 90% chance of being in sync
      
      dispatch({
        type: INVENTORY_ACTIONS.UPDATE_SYNC_STATUS,
        payload: {
          lastSync: new Date().toISOString(),
          inSync: isInSync,
          pendingOperations: isInSync ? 0 : Math.floor(Math.random() * 5)
        }
      });
      
    } catch (error) {
      console.error('Sync check failed:', error);
    }
  };

  // Load data on mount
  useEffect(() => {
    loadInventoryData();
    
    // Set up periodic sync checks
    const syncInterval = setInterval(checkSyncStatus, 30000); // Every 30 seconds
    
    return () => clearInterval(syncInterval);
  }, []);

  const value = {
    ...state,
    loadInventoryData,
    addInventoryItem,
    moveInventory,
    mintTokens,
    checkSyncStatus
  };

  return (
    <InventoryContext.Provider value={value}>
      {children}
    </InventoryContext.Provider>
  );
};

export const useInventory = () => {
  const context = useContext(InventoryContext);
  if (!context) {
    throw new Error('useInventory must be used within an InventoryProvider');
  }
  return context;
}; 